import { PhysioLayout } from "@/components/layout/physio-layout";
import { useGetNurseDashboard } from "@workspace/api-client-react";
import { Dumbbell, ClipboardList, Waves, HandMetal, Heart, BookOpen, MessageSquare, ArrowRight, GraduationCap, Shield, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { useState, useEffect } from "react";

const quickCards = [
  {
    href: "/physio/exercise-protocols",
    icon: Dumbbell,
    title: "Exercise & Rehab Protocols",
    desc: "Condition-specific exercise prescription for axSpA, RA, SLE, myopathy, and SSc",
    colour: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    href: "/physio/outcome-measures",
    icon: ClipboardList,
    title: "Outcome Measures",
    desc: "BASDAI, BASFI, BASMI, HAQ-DI, DASH, PSFS, VAS/NRS calculators and reference",
    colour: "text-teal-600",
    bg: "bg-teal-50",
  },
  {
    href: "/physio/hydrotherapy",
    icon: Waves,
    title: "Hydrotherapy",
    desc: "Aquatic physiotherapy protocols, safety guidance, and session structures",
    colour: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    href: "/physio/manual-therapy",
    icon: HandMetal,
    title: "Manual Therapy",
    desc: "Maitland grading, precautions by condition, soft tissue techniques",
    colour: "text-amber-600",
    bg: "bg-amber-50",
  },
  {
    href: "/physio/condition-protocols",
    icon: Heart,
    title: "Condition-Specific Protocols",
    desc: "Physio management summaries: goals, interventions, red flags, escalation",
    colour: "text-rose-600",
    bg: "bg-rose-50",
  },
  {
    href: "/physio/guidelines",
    icon: BookOpen,
    title: "CSP & NICE Guidelines",
    desc: "CSP guidance, NICE MSK guidelines, and ACPR rheumatology physiotherapy resources",
    colour: "text-indigo-600",
    bg: "bg-indigo-50",
  },
];

interface CpdSummary {
  count: number;
  totalHours: number;
  clinical: number;
  professional: number;
  educational: number;
  leadership: number;
}

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const today = new Date();
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function PhysioDashboard() {
  const { data: dashboard } = useGetNurseDashboard();
  const [cpdSummary, setCpdSummary] = useState<CpdSummary | null>(null);
  const [hcpcRenewalDate, setHcpcRenewalDate] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/physio/cpd/summary")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCpdSummary(d); })
      .catch(() => {});
    fetch("/api/physio/settings")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.hcpcRenewalDate) setHcpcRenewalDate(d.hcpcRenewalDate); })
      .catch(() => {});
  }, []);

  const recentGuidelines = dashboard?.recentGuidelines?.slice(0, 3) ?? [];

  return (
    <PhysioLayout title="Dashboard">
      <div className="space-y-8">
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-3xl p-8 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Dumbbell className="h-7 w-7 text-emerald-300" />
            <span className="text-emerald-300 font-medium text-sm uppercase tracking-wider">Physiotherapy Portal</span>
          </div>
          <h2 className="text-2xl font-bold mb-2">Welcome to RheumHub Physio</h2>
          <p className="text-emerald-200 text-sm max-w-xl">
            Evidence-based rehabilitation tools for UK rheumatology physiotherapists. Exercise protocols, outcome measures, hydrotherapy guidance, and a CSP-grounded AI clinical assistant.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Clinical Tools</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickCards.map((card) => (
              <Link key={card.href} href={card.href}>
                <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-emerald-200 transition-all cursor-pointer group h-full flex flex-col">
                  <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mb-4`}>
                    <card.icon className={`h-5 w-5 ${card.colour}`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-emerald-700 transition-colors">{card.title}</h3>
                  <p className="text-slate-500 text-sm flex-1">{card.desc}</p>
                  <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium mt-4">
                    Open <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap className="h-5 w-5 text-emerald-600" />
              <h3 className="font-bold text-slate-900">CPD Portfolio Summary</h3>
              <Link href="/physio/cpd" className="ml-auto text-xs text-emerald-700 hover:text-emerald-800 font-medium">
                View log →
              </Link>
            </div>
            {cpdSummary ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-emerald-50 rounded-xl p-3">
                    <p className="text-xl font-bold text-emerald-800">{cpdSummary.count}</p>
                    <p className="text-xs text-emerald-600">Activities logged this year</p>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-3">
                    <p className="text-xl font-bold text-teal-800">{cpdSummary.totalHours.toFixed(1)}</p>
                    <p className="text-xs text-teal-600">Total CPD hours</p>
                  </div>
                </div>
                {hcpcRenewalDate ? (() => {
                  const days = daysUntil(hcpcRenewalDate);
                  const urgency = days < 30 ? "red" : days < 90 ? "amber" : "emerald";
                  const colours = {
                    red: "bg-red-50 border-red-200 text-red-800",
                    amber: "bg-amber-50 border-amber-200 text-amber-800",
                    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
                  };
                  return (
                    <div className={`flex items-center justify-between rounded-xl border px-3 py-2 ${colours[urgency]}`}>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold">HCPC Renewal</p>
                          <p className="text-xs opacity-80">
                            {new Date(hcpcRenewalDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-bold whitespace-nowrap">
                        {days > 0 ? `${days}d` : days === 0 ? "Today" : `${Math.abs(days)}d overdue`}
                      </span>
                    </div>
                  );
                })() : (
                  <Link href="/physio/cpd/revalidation">
                    <div className="flex items-center gap-2 text-xs text-slate-500 hover:text-emerald-700 cursor-pointer">
                      <Shield className="h-3.5 w-3.5" />
                      <span>Set your HCPC renewal date →</span>
                    </div>
                  </Link>
                )}
                <div className="flex gap-2 flex-wrap">
                  <Link href="/physio/cpd/revalidation">
                    <button className="flex items-center gap-1.5 text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded-lg transition-colors">
                      <Shield className="h-3.5 w-3.5" /> HCPC Revalidation
                    </button>
                  </Link>
                  <Link href="/physio/career">
                    <button className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg transition-colors">
                      <TrendingUp className="h-3.5 w-3.5" /> Career Pathways
                    </button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-sm text-emerald-800">
                <p className="font-semibold mb-1">HCPC CPD Portfolio</p>
                <p className="text-emerald-700">Log CPD activities, track HCPC revalidation readiness, build your APP portfolio, and explore career development pathways.</p>
                <Link href="/physio/cpd">
                  <button className="mt-2 text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded-lg transition-colors">
                    Start CPD Log
                  </button>
                </Link>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-emerald-600" />
              <h3 className="font-bold text-slate-900">Recent Guideline Updates</h3>
            </div>
            {recentGuidelines.length > 0 ? (
              <div className="space-y-3">
                {recentGuidelines.map((g: { id: number; title: string; organisation: string }) => (
                  <Link key={g.id} href={`/physio/guidelines`}>
                    <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0 mt-2" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{g.title}</p>
                        <p className="text-xs text-slate-500">{g.organisation}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">No guideline updates available</div>
            )}
          </div>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-4">
          <MessageSquare className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold text-slate-900 mb-1">AI Clinical Assistant</h3>
            <p className="text-sm text-slate-600 mb-3">Ask rehabilitation and exercise prescription questions. Grounded in CSP Standards of Practice, NICE MSK guidelines, and ACPR guidance.</p>
            <Link href="/physio/chat">
              <button className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Open AI Assistant
              </button>
            </Link>
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
